/**
 * frontend/src/App.js
 * 
 * Main application component that defines the routing structure.
 * It uses React Router for navigation and Framer Motion for transitions.
 * Includes PrivateRoute and PublicRoute components for access control.
 * 
 * Common components:
 * - PrivateRoute: Protects routes that require authentication.
 * - PublicRoute: Redirects authenticated users away from public-only pages (like Login).
 * - App: Defines the route mappings for the entire application.
 */
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from './context/AuthContext';
import ScrollToTop from './components/common/ScrollToTop';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import GoogleCallback from './pages/GoogleCallback';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Templates from './pages/Templates';
import AuditLogs from './pages/AuditLogs';
import ChangeRequestDetail from './pages/ChangeRequestDetail';
import NewChangeRequest from './pages/NewChangeRequest';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Security from './pages/Security';
import Refunds from './pages/Refunds';
import Pricing from './pages/Pricing';
import AcceptInvite from './pages/AcceptInvite';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" /> : children;
}

function App() {
  return (
    <>
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <Routes>
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/dashboard/new" element={<PrivateRoute><NewChangeRequest /></PrivateRoute>} />
        <Route path="/dashboard/change/:changeId" element={<PrivateRoute><ChangeRequestDetail /></PrivateRoute>} />
        <Route path="/dashboard/templates" element={<PrivateRoute><Templates /></PrivateRoute>} />
        <Route path="/dashboard/audit-logs" element={<PrivateRoute><AuditLogs /></PrivateRoute>} />
        <Route path="/dashboard/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/security" element={<Security />} />
        <Route path="/refunds" element={<Refunds />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/" element={<Landing />} />
        </Routes>
      </AnimatePresence>
    </>
  );
}

export default App;
